import { useEffect, useRef, useCallback } from 'react';
import { io, Socket } from 'socket.io-client';
import { SOCKET_IO_PATH } from '../utils/socketConfig';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';
const SOCKET_BASE = API_BASE_URL.replace(/\/api\/?$/, '');

export type ChatSocketHandlers = {
  onMessage?: (payload: { conversationId: string; message: unknown }) => void;
  onConversationUpdated?: (payload: { conversation: unknown }) => void;
  onTyping?: (payload: {
    conversationId: string;
    userId: string;
    role: string;
    isTyping: boolean;
  }) => void;
  onMessagesRead?: (payload: {
    conversationId: string;
    readerRole: 'student' | 'admin';
    readAt: string;
  }) => void;
};

export const useChatSocket = (
  token: string | null,
  handlers: ChatSocketHandlers,
  enabled = true
) => {
  const socketRef = useRef<Socket | null>(null);
  const handlersRef = useRef(handlers);
  handlersRef.current = handlers;

  const emitTyping = useCallback((conversationId: string, isTyping: boolean) => {
    socketRef.current?.emit('chat:typing', { conversationId, isTyping });
  }, []);

  const joinConversation = useCallback((conversationId: string) => {
    socketRef.current?.emit('chat:join-conversation', { conversationId });
  }, []);

  const leaveConversation = useCallback((conversationId: string) => {
    socketRef.current?.emit('chat:leave-conversation', { conversationId });
  }, []);

  useEffect(() => {
    if (!enabled || !token) return;

    const socket = io(SOCKET_BASE, {
      path: SOCKET_IO_PATH,
      transports: ['websocket', 'polling'],
      auth: { token },
    });

    socket.on('chat:message', (payload) => handlersRef.current.onMessage?.(payload));
    socket.on('chat:conversation-updated', (payload) =>
      handlersRef.current.onConversationUpdated?.(payload)
    );
    socket.on('chat:typing', (payload) => handlersRef.current.onTyping?.(payload));
    socket.on('chat:messages-read', (payload) => handlersRef.current.onMessagesRead?.(payload));

    socketRef.current = socket;

    return () => {
      socket.disconnect();
      socketRef.current = null;
    };
  }, [token, enabled]);

  return { emitTyping, joinConversation, leaveConversation, socket: socketRef };
};

export default useChatSocket;
